import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { SkeletonBlock } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { ViewIcon, DeleteIcon } from '../components/ActionIcons.js';
import { BulkActionBar, BulkActionButton, BulkResultToast, postBulk, type BulkResult } from '../components/BulkActionBar.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { FileText } from 'lucide-react';
import { proposalStatusSchema, proposalTypeSchema, PROPOSAL_STATUS_LABELS, type ProposalOutput } from '@1financial/shared';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'pill-neutral',
  SENT: 'pill-info',
  VIEWED: 'pill-warn',
  SENT_BACK: 'pill-warn',
  SIGNED: 'pill-success',
  DECLINED: 'pill-danger',
  EXPIRED: 'pill-neutral',
};

const TYPE_BADGE: Record<string, string> = {
  RECURRING: 'pill-info',
  ONE_OFF: 'pill-neutral',
};

const TYPE_LABEL: Record<string, string> = {
  RECURRING: 'Recurring',
  ONE_OFF: 'One-off',
};

async function apiError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { ok?: false; error?: { message?: string; code?: string } };
    const msg = body?.error?.message ?? body?.error?.code ?? res.statusText;
    return new Error(msg ? `${res.status}: ${msg}` : `Failed: ${res.status}`);
  } catch {
    return new Error(text || res.statusText || `Failed: ${res.status}`);
  }
}

export function ProposalsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deleting, setDeleting] = useState<ProposalOutput | null>(null);
  const { canDo } = useCapabilities();
  const canBulkDelete = canDo('proposals.delete');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  // URL filters (for example dashboard tiles) take precedence over saved preferences.
  const [statusFilter, setStatusFilter] = useUserPreference('proposals.status', (() => {
    const v = new URLSearchParams(window.location.search).get('status') ?? '';
    return (proposalStatusSchema.options as readonly string[]).includes(v) ? v : '';
  })(), {
    urlKeys: ['status'],
    validate: (value) => typeof value === 'string' && (value === '' || (proposalStatusSchema.options as readonly string[]).includes(value)),
  });
  const [typeFilter, setTypeFilter] = useUserPreference('proposals.type', '', {
    validate: (value) => typeof value === 'string' && (value === '' || (proposalTypeSchema.options as readonly string[]).includes(value)),
  });
  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls('createdAt', 'desc', [statusFilter, typeFilter]);

  const activeFilters: ActiveFilter[] = [
    ...(statusFilter ? [{ key: 'status', label: `Status: ${statusFilter}` }] : []),
    ...(typeFilter ? [{ key: 'type', label: `Type: ${TYPE_LABEL[typeFilter] ?? typeFilter}` }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'status') setStatusFilter('');
    if (key === 'type') setTypeFilter('');
  }

  function clearAllFilters() {
    setStatusFilter('');
    setTypeFilter('');
  }

  const list = useQuery<{ items: ProposalOutput[]; total: number; nextCursor: string | null }>({
    queryKey: ['proposals', { status: statusFilter, type: typeFilter, sortBy, sortOrder, cursor }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`/v1/proposals?${params.toString()}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await apiError(res);
      const body = await res.json() as { ok: true; data: { items: ProposalOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const send = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/proposals/${id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await apiError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposals'] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/proposals/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await apiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      setDeleting(null);
    },
  });

  const items = list.data?.items ?? [];

  const allVisibleSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) items.forEach((p) => next.delete(p.id));
      else items.forEach((p) => next.add(p.id));
      return next;
    });
  }
  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function runBulkDelete() {
    setBulkBusy(true);
    try {
      const result = await postBulk('/v1/proposals/bulk', { action: 'delete', ids: [...selected] });
      setBulkResult(result);
      setSelected(new Set());
      setBulkDeleting(false);
      void queryClient.invalidateQueries({ queryKey: ['proposals'] });
    } finally {
      setBulkBusy(false);
    }
  }

  const formatFee = (p: ProposalOutput) => {
    if (p.feeAmount == null || !p.feeCurrency) return '—';
    const fee = `${p.feeCurrency} ${(p.feeAmount / 100).toFixed(2)}`;
    return p.type === 'RECURRING' ? `${fee} / period` : fee;
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Proposals</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} total</p>
        </div>
        <button
          onClick={() => navigate('/proposals/new')}
          disabled={!canDo('proposals.create') || !canDo('billing.view')}
          className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
        >
          + New proposal
        </button>
      </header>

      {deleting && (
        <ConfirmModal
          title="Delete proposal"
          message={`Delete "${deleting.subject}"? Soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {list.isLoading && <SkeletonBlock />}
      {list.error && <div className="text-sm text-rose-300">Error: {list.error.message}</div>}

      <FilterBar
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_BADGE).map((s) => (
            <option key={s} value={s}>{PROPOSAL_STATUS_LABELS[s] ?? s}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All types</option>
          {(proposalTypeSchema.options as readonly string[]).map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t] ?? t}</option>
          ))}
        </select>
      </FilterBar>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-bg-card border-b border-border">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                  className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                />
              </th>
              <th className="text-left px-4 py-3 text-muted font-medium">Recipients</th>
              <SortableTh label="Subject" sortKey="subject" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <th className="text-left px-4 py-3 text-muted font-medium">Type</th>
              <SortableTh label="Fee" sortKey="feeAmount" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <SortableTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <SortableTh label="Sent / Signed" sortKey="signedAt" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-muted" />
              <th className="text-left px-4 py-3 text-muted font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && !list.isLoading && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={8} onClearAll={clearAllFilters} />
                : <tr>
                    <td colSpan={8}>
                      <EmptyState icon={FileText} title="No proposals yet." subtitle="Compose one — recurring from compliance items or one-off from an engagement." />
                    </td>
                  </tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-bg-hover">
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelected(p.id)}
                    aria-label={`Select ${p.subject}`}
                    className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                  />
                </td>
                <td className="px-4 py-3 text-white">
                  <span className="block max-w-[180px] truncate" title={p.recipients.map((r) => r.name).join(', ')}>
                    {p.recipients.map((r) => r.name).join(', ')}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="block max-w-[240px] xl:max-w-[32rem] 2xl:max-w-[48rem] truncate text-white font-medium" title={p.subject}>{p.subject}</span>
                  <div className="text-xs text-muted">{p.clientName ?? '—'}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${TYPE_BADGE[p.type] ?? 'pill-neutral'}`}>
                    {TYPE_LABEL[p.type] ?? p.type}
                  </span>
                </td>
                <td className="px-4 py-3 text-white">{formatFee(p)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[p.status] ?? 'bg-bg-hover text-white'}`}>
                    {PROPOSAL_STATUS_LABELS[p.status] ?? p.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted">
                  {p.signedAt ? fmtDate(p.signedAt) : p.status === 'SENT' ? 'Sent' : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {canDo('proposals.send') && canDo('billing.view') && p.status === 'DRAFT' && (
                      <button
                        onClick={() => send.mutate(p.id)}
                        disabled={send.isPending && send.variables === p.id}
                        className="text-xs btn-ghost-info border px-2 py-1 rounded"
                      >
                        Send
                      </button>
                    )}
                    <ViewIcon to={`/proposals/${p.id}`} label="View proposal" />
                    <DeleteIcon
                      onClick={() => setDeleting(p)}
                      disabled={!canDo('proposals.delete') || (remove.isPending && remove.variables === p.id)}
                      label="Delete proposal"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={items.length}
          limit={50}
          total={list.data?.total}
          hasNext={Boolean(list.data?.nextCursor)}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>

      {selected.size > 0 && canBulkDelete && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          <BulkActionButton danger onClick={() => setBulkDeleting(true)}>Delete</BulkActionButton>
        </BulkActionBar>
      )}

      {bulkDeleting && (
        <ConfirmModal
          title="Delete proposals"
          message={`Delete ${selected.size} selected proposal(s)? They are soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setBulkDeleting(false)}
          onConfirm={() => void runBulkDelete()}
          error={null}
          submitting={bulkBusy}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {bulkResult && <BulkResultToast result={bulkResult} onClose={() => setBulkResult(null)} />}
    </div>
  );
}

function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting} className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}>{submitting ? 'Working…' : confirmText}</button>
        </div>
      </div>
    </div>
  );
}
